import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from "react";

import useDeviceFingerprint from "./useDeviceFingerPrint";

import { fetchLocalStorageItem, setLocalStorageItem } from "../Utils/StorageUtils";

import { passkeyRegisterAPI, passkeyRegisterInitAPI, passkeySignInAPI, passkeySignInInitAPI } from "../Services/APIs/Passkey";

import { 
  base64ToUint8Array, base64UrlToArrayBuffer, bufferToBase64Url, extractHostname, uint8ArrayToStandardBase64 
} from "../Utils/DataConvertUtils";

// FIXED: Export as a custom hook with 'use' prefix
export default function usePasskeyManager({ phoneNumber, name, onSuccess, onError }) {
  const [loading, setLoading] = useState(false);
  const [fingerprintSupported, setFingerprintSupported] = useState(false);
  const [credentialDisplayId, setCredentialDisplayId] = useState('');
  const visitorId = useDeviceFingerprint();

  // Check if passkey is already registered
  useEffect(() => {
    const credentialId = fetchLocalStorageItem('fingerprintCredentialId');
    if (credentialId) {
      setCredentialDisplayId(credentialId);
    }
  }, []);

  // FIXED: Better fingerprint support detection
  useEffect(() => {
    const checkSupport = async () => {
      try {
        // Check secure context
        if (!window.isSecureContext) {
          setFingerprintSupported(false);
          return;
        }

        // Check WebAuthn API
        if (!window.PublicKeyCredential) {
          setFingerprintSupported(false);
          return;
        }

        // Method 1: Check platform authenticator
        if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
          const platformAvailable = await window.PublicKeyCredential
            .isUserVerifyingPlatformAuthenticatorAvailable();
          
          if (platformAvailable) {
            setFingerprintSupported(true);
            return;
          }
        }

        // Method 2: Check conditional mediation (newer API)
        if (typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function') {
          const conditionalAvailable = await window.PublicKeyCredential
            .isConditionalMediationAvailable();
          
          if (conditionalAvailable) {
            setFingerprintSupported(true);
            return;
          }
        }

        // Fallback: Assume support exists
        setFingerprintSupported(true);

      } catch (error) {
        console.error('Error checking fingerprint support:', error);
        // Allow attempt even if check fails
        setFingerprintSupported(true);
      }
    };

    checkSupport();
  }, []);

  // Register passkey
  const registerPasskey = useCallback(async () => {
    try {
      setLoading(true);
      
      const initResponse = await passkeyRegisterInitAPI();
      
      if (!initResponse || !initResponse?.data?.options) {
        throw new Error('Failed to get registration options from server');
      }

      let options;
      try {
        options = typeof initResponse?.data?.options === 'string' 
          ? JSON.parse(initResponse.data.options) 
          : initResponse.data.options;
      } catch (parseError) {
        console.error('Failed to parse options:', parseError);
        throw new Error('Invalid options format from server');
      }

      const { challenge, user, rp, timeout, attestation, excludeCredentials } = options;

      if (!challenge) {
        throw new Error('No challenge received from server');
      }

      const challengeArray = base64ToUint8Array(challenge);
      
      let userIdArray;
      if (user?.id) {
        if (typeof user.id === 'string') {
          userIdArray = base64ToUint8Array(user.id);
        } else {
          userIdArray = new Uint8Array(user.id);
        }
      } else {
        const userIdString = visitorId || phoneNumber || `user_${Date.now()}`;
        const encoder = new TextEncoder();
        userIdArray = encoder.encode(userIdString).slice(0, 64);
      }

      const origin = window.location.origin;
      const rpId = window.location.hostname;

      // Enhanced authenticator selection for cross-platform
      const enhancedAuthenticatorSelection = {
        authenticatorAttachment: "platform",
        userVerification: "required",
        requireResidentKey: false,
        residentKey: "preferred"
      };

      // Enhanced pubKeyCredParams for better compatibility
      const enhancedPubKeyCredParams = [
        { alg: -7, type: "public-key" },  // ES256
        { alg: -257, type: "public-key" }, // RS256 (Windows Hello)
        { alg: -8, type: "public-key" },   // EdDSA
      ];

      const publicKeyCredentialCreationOptions = {
        challenge: challengeArray,
        rp: {
          name: rp?.name || "BankCo",
          id: rpId,
        },
        user: {
          id: userIdArray,
          name: user?.name || phoneNumber || "User",
          displayName: user?.displayName || user?.name || phoneNumber || "User",
        },
        pubKeyCredParams: enhancedPubKeyCredParams,
        authenticatorSelection: enhancedAuthenticatorSelection,
        timeout: timeout || 120000,
        attestation: attestation || 'none',
        excludeCredentials: excludeCredentials || [],
      };

      const credential = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      });

      if (!credential) {
        throw new Error('Failed to create credential');
      }

      const credIdBase64Url = credential.id;
      
      let rawIdStandardBase64 = '';
      if (credential.rawId instanceof ArrayBuffer) {
        rawIdStandardBase64 = uint8ArrayToStandardBase64(new Uint8Array(credential.rawId));
      } else {
        rawIdStandardBase64 = btoa(credential.id.replace(/-/g, '+').replace(/_/g, '/'));
      }

      const attestationStandardBase64 = uint8ArrayToStandardBase64(
        new Uint8Array(credential.response.attestationObject)
      );
      const clientDataStandardBase64 = uint8ArrayToStandardBase64(
        new Uint8Array(credential.response.clientDataJSON)
      );

      const userIdBase64Url = uint8ArrayToStandardBase64(userIdArray);

      const getDeviceName = () => {
        const ua = navigator.userAgent;
        
        if (ua.includes('Mac')) return `MacOS - ${name || 'User'}`;
        if (ua.includes('Windows')) return `Windows - ${name || 'User'}`;
        if (ua.includes('Android')) return `Android - ${name || 'User'}`;
        if (ua.includes('iPhone')) return `iPhone - ${name || 'User'}`;
        if (ua.includes('iPad')) return `iPad - ${name || 'User'}`;
        if (ua.includes('Linux')) return `Linux - ${name || 'User'}`;
        
        return `${navigator.platform || 'Unknown'} - ${name || 'User'}`;
      };

      const deviceName = getDeviceName();

      const verificationPayload = {
        credential: {
          id: credIdBase64Url,
          rawId: rawIdStandardBase64,
          type: credential.type || 'public-key',
          response: {
            attestationObject: attestationStandardBase64,
            clientDataJSON: clientDataStandardBase64,
            transports: typeof credential.response.getTransports === 'function'
              ? credential.response.getTransports()
              : ['internal', 'hybrid'],
          },
          authenticatorAttachment: credential.authenticatorAttachment || 'platform',
          clientExtensionResults: typeof credential.getClientExtensionResults === 'function'
            ? credential.getClientExtensionResults()
            : {},
        },
        name: deviceName,
        metadata: {
          origin: origin,
          rpId: rpId,
          userId: userIdBase64Url,
          userAgent: navigator.userAgent,
          platform: navigator.platform
        },
      };

      const verifyResponse = await passkeyRegisterAPI(verificationPayload);

      if (!verifyResponse || !verifyResponse?.data?.success) {
        throw new Error(verifyResponse?.data?.message || verifyResponse?.error || 'Failed to verify passkey');
      }

      setLocalStorageItem('fingerprintCredentialId', credIdBase64Url);
      setLocalStorageItem('fingerprintRpId', rpId);
      setLocalStorageItem('fingerprintVisitorId', visitorId);
      setLocalStorageItem('fingerprintOrigin', origin);
      
      const token = fetchLocalStorageItem('token') || fetchLocalStorageItem('fingerprintToken');
      if (token && phoneNumber) {
        setLocalStorageItem('fingerprintToken', token);
        setLocalStorageItem('fingerprintPhone', phoneNumber);
      }

      setCredentialDisplayId(credIdBase64Url);
      
      if (onSuccess) {
        onSuccess(credIdBase64Url);
      }

      return { success: true, credentialId: credIdBase64Url };
    } catch (error) {
      console.error('Passkey registration error:', error);
      
      let errorMessage;

      if (error?.data) {
        errorMessage = error?.data?.message || error?.data?.error || 'Server error';
      } else if (error.name === 'NotAllowedError') {
        errorMessage = 'Registration was cancelled or timed out. Please try again.';
      } else if (error.name === 'InvalidStateError') {
        errorMessage = 'This device may already have a passkey registered. Try removing it first.';
      } else if (error.name === 'NotSupportedError') {
        errorMessage = 'Biometric authentication is not supported on this device or browser.';
      } else if (error.name === 'SecurityError') {
        errorMessage = 'Security error. Please ensure you are on a secure connection (HTTPS).';
      } else if (error.name === 'AbortError') {
        errorMessage = 'Registration was aborted. Please try again.';
      } else {
        errorMessage = error?.message || 'Failed to register biometric';
      }
      
      if (onError) {
        onError(errorMessage);
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [visitorId, name, phoneNumber, onSuccess, onError]);

  // Remove passkey
  const removePasskey = useCallback(() => {
    try {
      localStorage.removeItem('fingerprintCredentialId');
      localStorage.removeItem('fingerprintRpId');
      localStorage.removeItem('fingerprintVisitorId');
      localStorage.removeItem('fingerprintOrigin');
      localStorage.removeItem('fingerprintToken');
      localStorage.removeItem('fingerprintPhone');
      localStorage.removeItem('isPasskeyPresent');

      setCredentialDisplayId('');
      
      return { success: true };
    } catch (error) {
      console.error('Error removing passkey:', error);
      return { success: false, error: 'Failed to remove passkey' };
    }
  }, []);

  // Return hook values (not JSX)
  return { loading, fingerprintSupported, credentialDisplayId, registerPasskey, removePasskey };
}

// PropTypes for the hook parameters
usePasskeyManager.propTypes = {
  phoneNumber: PropTypes.string,
  name: PropTypes.string,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
};

// Separate export functions for sign-in flow
export const handlePasskeySignInInit = async () => {
  const response = await passkeySignInInitAPI();
  if (response.status !== 200) throw new Error("Failed to init passkey");

  let options = response.data?.options ? JSON.parse(response.data.options) : response.data;
  if (options.rpId) options.rpId = extractHostname(options.rpId);
  return options;
};

export const handlePasskeyVerification = async (initOptions) => {
  if (!window.PublicKeyCredential) throw new Error("WebAuthn not supported");

  const publicKey = {
    challenge: base64UrlToArrayBuffer(initOptions.challenge),
    allowCredentials: initOptions.allowCredentials.map(c => ({
      type: "public-key",
      id: base64UrlToArrayBuffer(c.id),
      transports: c.transports || ["internal", "hybrid"],
    })),
    rpId: extractHostname(initOptions.rpId),
    userVerification: "required",
    timeout: 120000,
  };

  const assertion = await navigator.credentials.get({
    publicKey,
    mediation: "required"
  });

  if (!assertion) throw new Error("Authentication cancelled");

  return {
    credential: {
      id: assertion.id,
      rawId: bufferToBase64Url(assertion.rawId),
      type: "public-key",
      response: {
        authenticatorData: bufferToBase64Url(assertion.response.authenticatorData),
        clientDataJSON: bufferToBase64Url(assertion.response.clientDataJSON),
        signature: bufferToBase64Url(assertion.response.signature),
        userHandle: assertion.response.userHandle
          ? bufferToBase64Url(assertion.response.userHandle)
          : null,
      },
    },
  };
};

export const handlePasskeySignIn = async (assertionData) => {
  const response = await passkeySignInAPI(assertionData);
  if (response.status !== 200 || !response.data?.token) {
    throw new Error(response.data?.error || "Passkey login failed");
  }
  const token = response.data.token;
  sessionStorage.setItem("passkeyToken", token);
  return { success: true, token };
};